// ============================================================================
// AddEntrySheet — the trainer's "click a day → add" sheet (Wave 8, spec §B).
// ----------------------------------------------------------------------------
// Clicking a day cell on the consolidated trainer calendar (dashboard +
// CalendarPage) opens this mobile-first sheet, prefilled with the clicked date.
// The trainer:
//   1. picks a CLIENT (reusing the searchable picker pattern), then
//   2. picks ONE entry kind to create on that day:
//        • a scheduled PT(PA) session  → POST /api/sessions   (useCreateSession)
//        • a weekly goal for that week → PUT /api/clients/{id}/weekly-goal (upsert)
//        • homework (suggested activity) → POST /api/activities (useSuggestActivity)
// On save the existing mutation invalidations live-update the calendar (the
// shared React Query cache fans the change to the grid + every device), then the
// sheet closes. Pure reuse of the create endpoints — no new API surface.
//
// Mobile-first, Pulse, EN + 繁中, reduced-motion aware. Every user-facing string
// is an i18n key (dashboard:add.*) — NEVER a server string.
// ============================================================================
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import {
  Avatar,
  Banner,
  Button,
  SelectField,
  TextField,
} from "../components";
import { IconClock, IconStar } from "../components/icons";
import { useAccount } from "../hooks/account";
import { useClients } from "../hooks/clients";
import { useCreateSession } from "../hooks/sessions";
import { useSuggestActivity } from "../hooks/activities";
import { useUpsertWeeklyGoal } from "../hooks/weeklyGoals";
import type {
  ActivityInput,
  Client,
  ExerciseTarget,
  SessionInput,
  WeeklyGoalInput,
} from "../api";
import {
  computeSessionFee,
  currencySymbol,
  formatMoney,
  isRateType,
  roundFee,
} from "../lib/currency";
import { formatDayLong, startOfWeek } from "../lib/date";
import { Sheet } from "./Sheet";
import { cn } from "../lib/cn";

/** The three things the trainer can add on a day. */
type AddKind = "session" | "goal" | "homework";

const DURATION_PRESETS = [30, 45, 60, 90];
const TYPE_OPTIONS = ["strength", "cardio", "mobility", "hiit", "mixed"] as const;

function p2(n: number): string {
  return String(n).padStart(2, "0");
}

/** A Date + "HH:mm" → the <datetime-local> value (local wall-clock). */
function dayTimeToLocal(day: Date, time: string): string {
  const t = /^\d{2}:\d{2}$/.test(time) ? time : "09:00";
  return `${day.getFullYear()}-${p2(day.getMonth() + 1)}-${p2(day.getDate())}T${t}`;
}

/** <datetime-local> (device-local) → timezone-aware UTC ISO the API stores. */
function localToIso(v: string): string | null {
  const s = v.trim();
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** A Date → local 'YYYY-MM-DD' (the weekly-goal `week` anchor + homework day). */
function dayKey(day: Date): string {
  return `${day.getFullYear()}-${p2(day.getMonth() + 1)}-${p2(day.getDate())}`;
}

function numOrUndef(s: string): number | undefined {
  const v = s.trim();
  if (v === "") return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
}

function parseNum(v: string): number | null {
  const s = v.trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function parseInt0(v: string): number | null {
  const n = parseNum(v);
  if (n == null) return null;
  const i = Math.trunc(n);
  return i >= 0 ? i : null;
}

export interface AddEntrySheetProps {
  /** The clicked day (prefills the date/week); null closes the sheet. */
  day: Date | null;
  onClose: () => void;
}

export function AddEntrySheet({ day, onClose }: AddEntrySheetProps) {
  const { t } = useTranslation("dashboard");
  const [clientId, setClientId] = useState<number | null>(null);
  const [pickedClient, setPickedClient] = useState<Client | null>(null);
  const [kind, setKind] = useState<AddKind>("session");

  // Re-seed the picker each time a fresh day opens (start clientless, on session).
  useEffect(() => {
    if (!day) return;
    setClientId(null);
    setPickedClient(null);
    setKind("session");
  }, [day]);

  const dayLabel = day ? formatDayLong(day) : "";

  return (
    <Sheet
      open={day != null}
      onClose={onClose}
      size="lg"
      title={t("add.title")}
      subtitle={dayLabel ? t("add.onDay", { day: dayLabel }) : undefined}
    >
      {!day ? null : (
        <div className="space-y-5 py-1">
          {/* 1 — Client picker */}
          <div>
            <p className="mb-1.5 font-display text-sm font-bold text-body">
              {t("add.client")}
              <span className="ml-1 text-ember">*</span>
            </p>
            <AddClientPicker
              value={clientId}
              onChange={(id, c) => {
                setClientId(id);
                setPickedClient(c);
              }}
            />
          </div>

          {/* 2 — Entry-kind selector */}
          <div>
            <p className="mb-1.5 font-display text-sm font-bold text-body">
              {t("add.whatToAdd")}
            </p>
            <KindToggle value={kind} onChange={setKind} />
          </div>

          {/* 3 — The chosen kind's inline form (date prefilled from the day) */}
          {clientId == null ? (
            <Banner tone="info" title={t("add.pickClientFirst")} />
          ) : kind === "session" ? (
            <SessionFields
              day={day}
              clientId={clientId}
              client={pickedClient}
              onClose={onClose}
            />
          ) : kind === "goal" ? (
            <GoalFields day={day} clientId={clientId} onClose={onClose} />
          ) : (
            <HomeworkFields day={day} clientId={clientId} onClose={onClose} />
          )}
        </div>
      )}
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Client picker (compact, searchable) — dashboard-local twin of the sessions one.
// ---------------------------------------------------------------------------
function AddClientPicker({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (clientId: number, client: Client) => void;
}) {
  const { t } = useTranslation("dashboard");
  const reduce = useReducedMotion();
  const clientsQuery = useClients(false);
  const [query, setQuery] = useState("");

  const rows = useMemo(() => {
    const list = (clientsQuery.data ?? []).filter((c) => c.status === "active");
    const q = query.trim().toLowerCase();
    const filtered = q ? list.filter((c) => c.name.toLowerCase().includes(q)) : list;
    return [...filtered].sort((a, b) => {
      if (a.starred !== b.starred) return b.starred - a.starred;
      return a.name.localeCompare(b.name);
    });
  }, [clientsQuery.data, query]);

  return (
    <div className="space-y-3">
      <TextField
        label={t("add.searchClients")}
        hideLabel
        placeholder={t("add.searchClients")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        inputMode="search"
        iconLeft={
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
            <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="2" />
            <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        }
      />
      {clientsQuery.isLoading ? (
        <p className="py-5 text-center text-sm text-body-soft">{t("add.loading")}</p>
      ) : rows.length === 0 ? (
        <p className="py-5 text-center text-sm text-body-soft">{t("add.noClients")}</p>
      ) : (
        <ul className="max-h-44 space-y-2 overflow-y-auto pr-1">
          {rows.map((c, i) => {
            const active = value === c.id;
            return (
              <motion.li
                key={c.id}
                initial={reduce ? false : { opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.16, delay: reduce ? 0 : Math.min(i * 0.02, 0.18) }}
              >
                <button
                  type="button"
                  onClick={() => onChange(c.id, c)}
                  aria-pressed={active}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-md p-2.5 text-left ring-1 transition-all duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt",
                    active
                      ? "bg-coral-soft ring-coral/40"
                      : "bg-surface ring-line hover:-translate-y-0.5 hover:shadow-sm",
                  )}
                >
                  <Avatar name={c.name} size="sm" />
                  <span className="min-w-0 flex-1 truncate font-display text-sm font-bold text-body">
                    {c.name}
                  </span>
                  {c.starred === 1 && (
                    <IconStar width={16} height={16} filled className="text-amber" />
                  )}
                  {active && (
                    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-coral" fill="none" aria-hidden>
                      <path d="m5 12 5 5 9-11" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              </motion.li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Entry-kind segmented control.
// ---------------------------------------------------------------------------
function KindToggle({
  value,
  onChange,
}: {
  value: AddKind;
  onChange: (k: AddKind) => void;
}) {
  const { t } = useTranslation("dashboard");
  const kinds: AddKind[] = ["session", "goal", "homework"];
  return (
    <div className="grid grid-cols-3 gap-1 rounded-pill bg-surface-sunken p-1">
      {kinds.map((k) => {
        const active = value === k;
        return (
          <button
            key={k}
            type="button"
            onClick={() => onChange(k)}
            aria-pressed={active}
            className={cn(
              "inline-flex items-center justify-center gap-1.5 rounded-pill px-2 py-2 font-display text-sm font-bold transition-all duration-fast",
              active ? "bg-grad-coral text-white shadow-volt" : "text-body-soft hover:text-body",
            )}
          >
            <span className="truncate">{t(`add.kind.${k}`)}</span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SESSION fields — schedules a planned PT session on the clicked day.
// ---------------------------------------------------------------------------
function SessionFields({
  day,
  clientId,
  client,
  onClose,
}: {
  day: Date;
  clientId: number;
  client: Client | null;
  onClose: () => void;
}) {
  const { t } = useTranslation("dashboard");
  const account = useAccount();
  const create = useCreateSession();

  const [time, setTime] = useState("09:00");
  const [duration, setDuration] = useState("60");
  const [type, setType] = useState<string>("strength");
  const [location, setLocation] = useState("");
  const [fee, setFee] = useState("");
  const [feeOverridden, setFeeOverridden] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const currency = (account.data?.default_currency || "").trim();
  const symbol = currencySymbol(currency);
  const durationMin = parseInt0(duration);

  // Auto-fee from the client's charge model (matches SessionForm behaviour).
  const autoFee = useMemo(() => {
    if (!client || !isRateType(client.rate_type)) return null;
    const f = computeSessionFee(client.rate_type, client.rate_amount, null, durationMin);
    return f == null ? null : roundFee(f);
  }, [client, durationMin]);

  useEffect(() => {
    if (feeOverridden) return;
    setFee(autoFee != null ? String(autoFee) : "");
  }, [autoFee, feeOverridden]);

  const hasRate = client != null && isRateType(client.rate_type);
  const rateHint = hasRate
    ? client!.rate_type === "per_hour"
      ? t("detail.feeAutoHourly", { rate: formatMoney(client!.rate_amount, currency, { symbol }) })
      : t("detail.feeAutoSession", { rate: formatMoney(client!.rate_amount, currency, { symbol }) })
    : undefined;

  const submit = () => {
    setError(undefined);
    const iso = localToIso(dayTimeToLocal(day, time));
    const body: SessionInput = {
      status: "planned",
      scheduled_at: iso,
      performed_at: null,
      duration_min: parseInt0(duration),
      location_text: location.trim() || null,
      type: type.trim() || null,
      fee_amount: parseNum(fee),
      currency: currency || null,
    };
    create.mutate(
      { clientId, body },
      {
        onSuccess: () => onClose(),
        onError: () => setError(t("add.saveError")),
      },
    );
  };

  return (
    <div className="space-y-4 rounded-md bg-surface-soft p-4 ring-1 ring-line">
      {/* Time */}
      <div>
        <label htmlFor="vf-add-time" className="mb-1.5 block font-display text-sm font-bold text-body">
          {t("add.time")}
        </label>
        <div className="relative">
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-body-faint">
            <IconClock width={18} height={18} />
          </span>
          <input
            id="vf-add-time"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="h-12 w-full rounded-md bg-surface pl-10 pr-4 text-base text-body ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none"
          />
        </div>
      </div>

      {/* Duration */}
      <div>
        <p className="mb-1.5 font-display text-sm font-bold text-body">{t("detail.duration")}</p>
        <div className="flex flex-wrap items-center gap-2">
          {DURATION_PRESETS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDuration(String(d))}
              aria-pressed={duration.trim() === String(d)}
              className={cn(
                "rounded-pill px-4 py-2 text-sm font-bold transition-all duration-fast",
                duration.trim() === String(d)
                  ? "bg-grad-coral text-white shadow-volt"
                  : "bg-surface-sunken text-body-muted ring-1 ring-inset ring-line hover:bg-line",
              )}
            >
              {d}
            </button>
          ))}
          <div className="w-24">
            <TextField
              label={t("detail.duration")}
              hideLabel
              type="number"
              inputMode="numeric"
              min={0}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              suffix={t("detail.minutes")}
            />
          </div>
        </div>
      </div>

      {/* Type + Location */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField
          label={t("detail.type")}
          value={type}
          onChange={(e) => setType(e.target.value)}
          options={TYPE_OPTIONS.map((tp) => ({ label: t(`sessionType.${tp}`), value: tp }))}
        />
        <TextField
          label={t("detail.location")}
          placeholder={t("detail.locationPlaceholder")}
          value={location}
          onChange={(e) => setLocation(e.target.value)}
        />
      </div>

      {/* Fee */}
      <div>
        <TextField
          label={t("detail.fee")}
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          value={fee}
          onChange={(e) => {
            setFee(e.target.value);
            setFeeOverridden(true);
          }}
          hint={!feeOverridden ? rateHint : undefined}
          suffix={symbol || undefined}
        />
        {feeOverridden && autoFee != null && (
          <button
            type="button"
            onClick={() => setFeeOverridden(false)}
            className="mt-1.5 text-sm font-bold text-coral transition-colors hover:text-coral-600 focus-visible:outline-none focus-visible:underline"
          >
            {t("detail.feeResetAuto", { amount: formatMoney(autoFee, currency, { symbol }) })}
          </button>
        )}
      </div>

      {error && <Banner tone="danger" title={error} />}

      <SaveBar busy={create.isPending} onSave={submit} onCancel={onClose} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// WEEKLY-GOAL fields — sets the goal for the clicked day's week (Monday anchor).
// ---------------------------------------------------------------------------
function GoalFields({
  day,
  clientId,
  onClose,
}: {
  day: Date;
  clientId: number;
  onClose: () => void;
}) {
  const { t } = useTranslation("dashboard");
  const upsert = useUpsertWeeklyGoal();

  const [kcal, setKcal] = useState("");
  const [protein, setProtein] = useState("");
  const [water, setWater] = useState("");
  const [exType, setExType] = useState<string>("strength");
  const [exCount, setExCount] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | undefined>();

  // The Monday of the clicked day's week — the goal anchors here.
  const week = dayKey(startOfWeek(day));
  const weekLabel = formatDayLong(startOfWeek(day));

  const submit = () => {
    setError(undefined);
    const targets: ExerciseTarget[] = [];
    const cnt = numOrUndef(exCount);
    if (exType.trim() && cnt !== undefined && cnt > 0) {
      targets.push({ type: exType.trim(), count: cnt });
    }
    const body: WeeklyGoalInput = {
      week,
      kcal_target: numOrUndef(kcal) ?? null,
      protein_target_g: numOrUndef(protein) ?? null,
      water_target_ml: numOrUndef(water) ?? null,
      exercise_targets: targets,
      notes: notes.trim() || null,
    };
    upsert.mutate(
      { clientId, body },
      {
        onSuccess: () => onClose(),
        onError: () => setError(t("add.saveError")),
      },
    );
  };

  return (
    <div className="space-y-4 rounded-md bg-surface-soft p-4 ring-1 ring-line">
      <Banner tone="info" title={t("add.goal.weekHint", { date: weekLabel })} />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <TextField
          label={t("add.goal.kcal")}
          value={kcal}
          onChange={(e) => setKcal(e.target.value)}
          type="number"
          inputMode="numeric"
          min={0}
        />
        <TextField
          label={t("add.goal.protein")}
          value={protein}
          onChange={(e) => setProtein(e.target.value)}
          type="number"
          inputMode="numeric"
          min={0}
        />
        <TextField
          label={t("add.goal.water")}
          value={water}
          onChange={(e) => setWater(e.target.value)}
          type="number"
          inputMode="numeric"
          min={0}
        />
      </div>

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <SelectField
            label={t("add.goal.exerciseType")}
            value={exType}
            onChange={(e) => setExType(e.target.value)}
            options={TYPE_OPTIONS.map((tp) => ({ label: t(`sessionType.${tp}`), value: tp }))}
          />
        </div>
        <div className="w-28">
          <TextField
            label={t("add.goal.exerciseCount")}
            value={exCount}
            onChange={(e) => setExCount(e.target.value)}
            type="number"
            inputMode="numeric"
            min={1}
          />
        </div>
      </div>

      <div>
        <label htmlFor="vf-add-goal-notes" className="mb-1.5 block font-display text-sm font-bold text-body">
          {t("add.goal.notes")}
        </label>
        <textarea
          id="vf-add-goal-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full resize-y rounded-md bg-surface px-4 py-3 text-base text-body placeholder:text-body-faint ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none"
        />
      </div>

      {error && <Banner tone="danger" title={error} />}

      <SaveBar busy={upsert.isPending} onSave={submit} onCancel={onClose} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// HOMEWORK fields — prescribes a suggested activity scheduled on the clicked day.
// ---------------------------------------------------------------------------
function HomeworkFields({
  day,
  clientId,
  onClose,
}: {
  day: Date;
  clientId: number;
  onClose: () => void;
}) {
  const { t } = useTranslation("dashboard");
  const suggest = useSuggestActivity();

  const [title, setTitle] = useState("");
  const [type, setType] = useState("");
  const [sets, setSets] = useState("");
  const [reps, setReps] = useState("");
  const [duration, setDuration] = useState("");
  const [notes, setNotes] = useState("");
  const [needTitle, setNeedTitle] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const submit = () => {
    setError(undefined);
    setNeedTitle(false);
    const freeTitle = title.trim();
    if (!freeTitle) {
      setNeedTitle(true);
      return;
    }
    // Schedule at local midnight on the clicked day (the calendar buckets by day).
    const iso = localToIso(`${dayKey(day)}T00:00`);
    const body: ActivityInput = { title: freeTitle, scheduled_at: iso };
    if (type.trim()) body.type = type.trim();
    const setsN = numOrUndef(sets);
    if (setsN !== undefined) body.sets = setsN;
    const repsN = numOrUndef(reps);
    if (repsN !== undefined) body.reps = repsN;
    const durN = numOrUndef(duration);
    if (durN !== undefined) body.duration_min = durN;
    if (notes.trim()) body.notes = notes.trim();

    suggest.mutate(
      { clientId, body },
      {
        onSuccess: () => onClose(),
        onError: () => setError(t("add.saveError")),
      },
    );
  };

  return (
    <div className="space-y-4 rounded-md bg-surface-soft p-4 ring-1 ring-line">
      <TextField
        label={t("add.homework.title")}
        placeholder={t("add.homework.titlePlaceholder")}
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
          if (needTitle) setNeedTitle(false);
        }}
      />
      <TextField
        label={t("add.homework.type")}
        placeholder={t("add.homework.typePlaceholder")}
        value={type}
        onChange={(e) => setType(e.target.value)}
      />
      <div className="grid grid-cols-3 gap-4">
        <TextField
          label={t("add.homework.sets")}
          value={sets}
          onChange={(e) => setSets(e.target.value)}
          type="number"
          inputMode="numeric"
          min={0}
        />
        <TextField
          label={t("add.homework.reps")}
          value={reps}
          onChange={(e) => setReps(e.target.value)}
          type="number"
          inputMode="numeric"
          min={0}
        />
        <TextField
          label={t("add.homework.duration")}
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
          type="number"
          inputMode="numeric"
          min={0}
          suffix={t("detail.minutes")}
        />
      </div>
      <div>
        <label htmlFor="vf-add-hw-notes" className="mb-1.5 block font-display text-sm font-bold text-body">
          {t("add.homework.notes")}
        </label>
        <textarea
          id="vf-add-hw-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full resize-y rounded-md bg-surface px-4 py-3 text-base text-body placeholder:text-body-faint ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none"
        />
      </div>

      {needTitle && <Banner tone="warning" title={t("add.homework.needTitle")} />}
      {error && <Banner tone="danger" title={error} />}

      <SaveBar busy={suggest.isPending} onSave={submit} onCancel={onClose} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared save / cancel bar.
// ---------------------------------------------------------------------------
function SaveBar({
  busy,
  onSave,
  onCancel,
}: {
  busy: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation("dashboard");
  return (
    <div className="flex items-center gap-3 pt-1">
      <Button variant="ghost" onClick={onCancel} disabled={busy}>
        {t("add.cancel")}
      </Button>
      <div className="flex-1" />
      <Button onClick={onSave} disabled={busy}>
        {busy ? t("add.saving") : t("add.save")}
      </Button>
    </div>
  );
}

export type { AddKind };
